<div mat-dialog-title class="dialog-title p-3 pr-5 clearfix">
  <h5>
    Select {{ utils.getLangTerm('hazardous-substances.plural', 'Hazardous Substances') }}
    <span *ngIf="dataSource.selection.selected.length">
      ({{ dataSource.selection.selected.length }})
    </span>
  </h5>

  <button
    mat-icon-button
    aria-label="Close dialog"
    matTooltip="Close this dialog."
    (click)="onClose()"
    class="dialog-close-button">
    <mat-icon>close</mat-icon>
  </button>

  <form class="form-inline my-2 my-lg-0 float-lg-right" (ngSubmit)="onSearch()">
    <input
      class="form-control mr-2 my-1"
      type="search"
      [(ngModel)]="dataSource.search"
      name="search"
      placeholder="Search"
      aria-label="Search"/>

    <button
      mat-raised-button
      color="secondary"
      class="mr-2 mr-lg-3 my-1"
      type="submit"
      matTooltip="Run the current search">
      <mat-icon>search</mat-icon> Search
    </button>

    <button mat-raised-button class="mr-2 my-1" (click)="onClearSelected()" type="button">
      Clear Selected
    </button>

    <!--
      "Select all linked to this site" bulk control (R9.3, R9.4).

      Rendered only in site mode (truthy `site_id`) and only when at least one substance
      ID is in `siteLinkedIds`. The handler adds every site-linked ID to the current
      selection without removing any existing selections (strict union semantics). When
      the site fetch fails, times out, returns empty, or has no `site_id`, `siteLinkedIds`
      stays empty and this button is omitted entirely. In template mode (no `site_id`)
      the control never renders (R16.4).
    -->
    <button
      *ngIf="isSiteMode && siteLinkedIds.size > 0"
      mat-stroked-button
      color="accent"
      class="mr-2 my-1"
      type="button"
      matTooltip="Add every hazardous substance already linked to this site to your selection."
      (click)="onSelectAllLinkedToThisSite()">
      <mat-icon>playlist_add_check</mat-icon>
      Select all linked to this site
    </button>
  </form>
</div>

<hr class="m-0 p-0">

<mat-dialog-content>
  <!--
    Account list error: render an inline error banner in place of the table. Cancel
    remains wired (the dialog header retains its close button); confirm is suppressed
    by `onSaveAndClose()` while `loadFailed === true`.
  -->
  <div class="alert alert-danger m-3" role="alert" *ngIf="loadFailed">
    <mat-icon class="align-middle mr-2">error_outline</mat-icon>
    <span class="align-middle">
      We could not load the hazardous substances list. Please close this dialog and try again.
    </span>
  </div>

  <!--
    Empty account catalogue: render an empty-state message in place of the table once
    the first load completes successfully and the result set is empty. Cancel remains
    wired through the dialog header.
  -->
  <div
    class="alert alert-info m-3"
    role="status"
    *ngIf="!loadFailed && hasLoadedOnce && !loading && total === 0">
    <mat-icon class="align-middle mr-2">info</mat-icon>
    <span class="align-middle">
      No hazardous substances are available in this account yet. Add hazardous substances
      from the account-level Hazardous Substances list before linking them to a SWMS/RAMS.
    </span>
  </div>

  <table
    mat-table
    [dataSource]="dataSource"
    matSort
    matSortActive="substance"
    matSortDirection="asc"
    matSortDisableClear
    *ngIf="!loadFailed && (loading || total > 0 || !hasLoadedOnce)">
    <ng-container matColumnDef="select">
      <th mat-header-cell *matHeaderCellDef [style.width.px]="65">
        <mat-checkbox
          *ngIf="data['multiple']"
          (change)="$event ? dataSource.selectAllToggle() : null"
          [checked]="dataSource.selection.hasValue() && dataSource.isAllSelected()"
          [indeterminate]="dataSource.selection.hasValue() && !dataSource.isAllSelected()">
        </mat-checkbox>
      </th>
      <td mat-cell *matCellDef="let row">
        <mat-checkbox
          (click)="$event.stopPropagation()"
          (change)="$event ? dataSource.selection.toggle(row.id) : null"
          [checked]="dataSource.selection.isSelected(row.id)"
          appMarginRight>
        </mat-checkbox>
      </td>
    </ng-container>

    <ng-container matColumnDef="id">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>#</th>
      <td mat-cell *matCellDef="let element" class="text-wrap">
        {{ element.id }}
      </td>
    </ng-container>

    <ng-container matColumnDef="substance">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Substance</th>
      <td mat-cell *matCellDef="let element" class="text-wrap">
        {{ element.substance }}
      </td>
    </ng-container>

    <!--
      Hazard classification: shows `hazardous_substance_type` when present, falling back
      to `un_classification` and then `hsno_classification`. This mirrors the fallback
      chain used by the existing site Hazardous Substances list UI per R14.2.
    -->
    <ng-container matColumnDef="hazardous_substance_type">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Classification</th>
      <td mat-cell *matCellDef="let element" class="text-wrap">
        {{ element.hazardous_substance_type || element.un_classification || element.hsno_classification }}
      </td>
    </ng-container>

    <!--
      Non-sortable virtual column. Its content depends on the badge mode:

      Site mode (truthy `site_id`): renders a "Linked to this site" badge on each row
      whose ID appears in `siteLinkedIds`, populated by the site auto-suggest fetch on
      dialog open (R9.2). The fetch degrades silently to an empty set on missing site
      context, non-2xx response, network error, or 5-second timeout, so failure modes
      simply produce no badges.

      Template mode (no `site_id`): renders an informational "Linked to site(s)" badge on
      each row whose `sites_count` is greater than zero, driven solely by the
      `sites_count` value carried on the `v1/hazardous_substances` response (R16.2, R16.3).
      Rows whose `sites_count` is zero or missing render no badge.

      In both modes the chip does NOT change the row's selection state; it is purely
      informational (R16.4).
    -->
    <ng-container matColumnDef="linked_to_site">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let element" class="text-wrap">
        <mat-chip-set
          *ngIf="isSiteMode && siteLinkedIds.has(element.id)"
          role="list"
          aria-label="Linked to this site">
          <mat-chip
            role="listitem"
            color="accent"
            aria-label="Linked to this site">
            Linked to this site
          </mat-chip>
        </mat-chip-set>

        <mat-chip-set
          *ngIf="!isSiteMode && element.sites_count > 0"
          role="list"
          aria-label="Linked to site(s)">
          <mat-chip
            role="listitem"
            color="accent"
            aria-label="Linked to site(s)">
            Linked to site(s)
          </mat-chip>
        </mat-chip-set>
      </td>
    </ng-container>

    <ng-container matColumnDef="date_created">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Created</th>
      <td mat-cell *matCellDef="let element" class="text-wrap">
        {{ (element.date_created * 1000) | timezoneConvertMedium }}
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="dataSource.selection.toggle(row.id)">
    </tr>
  </table>
</mat-dialog-content>

<hr class="m-0 p-0">

<mat-dialog-actions class="d-flex justify-content-end px-3 py-1">
  <mat-paginator
    class="my-2 ml-1 mr-2"
    [length]="dataSource?.total | async"
    [pageSize]="dataSource.limit"
    [pageSizeOptions]="dataSource.pageSizeOptions"
    showFirstLastButtons>
  </mat-paginator>

  <button
    class="my-2 mx-1"
    mat-raised-button
    color="primary"
    type="button"
    (click)="onSaveAndClose()"
    [disabled]="loadFailed || (!data['multiple'] && !dataSource.selection.hasValue())">
    Select &amp; Close
  </button>
</mat-dialog-actions>
